/**
 * @file        src/lib/amenityIcons.js
 * @description Maps a (live) amenity/facility name to a relevant SVG icon path.
 *              Keyword-matched so any supplier wording resolves to a sensible
 *              glyph; falls back to a checkmark when nothing matches. Returns the
 *              `d` of a single 24×24 path so callers can drop it straight into
 *              an <svg viewBox="0 0 24 24"><path d={...}/></svg>.
 * @author      Kinetika Labs
 * @created     2026-06-24
 */

const ICONS = {
  check: 'm9 16.2-3.5-3.5L4 14.2 9 19l11-11-1.4-1.4z',
  wifi: 'M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3a4.24 4.24 0 0 0-6 0zm-4-4l2 2a7.07 7.07 0 0 1 10 0l2-2C15.14 9.14 8.87 9.14 5 13z',
  pool: 'M2 15c1.4 0 1.4 1 2.8 1 1.4 0 1.4-1 2.9-1 1.4 0 1.4 1 2.8 1 1.4 0 1.5-1 2.9-1s1.4 1 2.8 1c1.4 0 1.5-1 2.9-1v2c-1.4 0-1.5 1-2.9 1s-1.4-1-2.8-1c-1.4 0-1.5 1-2.9 1-1.4 0-1.4-1-2.8-1-1.5 0-1.5 1-2.9 1-1.4 0-1.4-1-2.8-1v-2zm5-2.8V6a2 2 0 0 1 4 0h-2c0-.6-.9-.6-.9 0v6.5c-.4-.1-.7-.2-1.1-.3zm6 0V6a2 2 0 0 1 4 0h-2c0-.6-.9-.6-.9 0v6.2c-.4.1-.7.2-1.1.3z',
  bar: 'M21 5V3H3v2l8 9v5H6v2h12v-2h-5v-5l8-9zM7.4 7 5.7 5h12.7l-1.8 2H7.4z',
  coffee: 'M20 3H4v10a4 4 0 0 0 4 4h6a4 4 0 0 0 4-4v-3h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 5h-2V5h2v3zM4 19h16v2H4z',
  restaurant: 'M11 9H9V2H7v7H5V2H3v7c0 2.1 1.7 3.8 3.8 4V22h2.4v-9c2.1-.2 3.8-1.9 3.8-4V2h-2v7zm5-3v8h2.5v8H21V2c-2.8 0-5 2.2-5 4z',
  parking: 'M13 3H6v18h4v-6h3a6 6 0 0 0 0-12zm.2 8H10V7h3.2a2 2 0 0 1 0 4z',
  gym: 'M20.6 6.4 17.8 3.6l-1.4 1.4-2.1-2.1-1.4 1.4 1.4 1.4-7.8 7.8-1.4-1.4L3.6 13l2.1 2.1L4.3 16.5l1.4 1.4 2.1-2.1 1.4 1.4 1.4-1.4-1.4-1.4 7.8-7.8 1.4 1.4 1.4-1.4-1.4-1.4 1.4-1.4z',
  spa: 'M12 5.5c1.7 2 2.7 4.5 2.7 7.2 0 .7-.1 1.4-.2 2.1-.8-.4-1.6-.9-2.5-.9s-1.7.5-2.5.9c-.1-.7-.2-1.4-.2-2.1 0-2.7 1-5.2 2.7-7.2M12 2S7.3 6 7.3 12.7c0 2 .6 3.8 1.5 5.3.9-.6 2-1 3.2-1s2.3.4 3.2 1c.9-1.5 1.5-3.3 1.5-5.3C16.7 6 12 2 12 2zM5 18c0 2.2 3.1 4 7 4s7-1.8 7-4c-2 .9-4.4 1.4-7 1.4S7 18.9 5 18z',
  laundry: 'M9.2 16.8a4 4 0 0 0 5.6-5.6l-5.6 5.6zM18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM7 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm3 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm2 16a6 6 0 1 1 0-12 6 6 0 0 1 0 12z',
  smoke: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20m0 2c1.8 0 3.5.6 4.9 1.7L5.7 16.9A7.9 7.9 0 0 1 12 4m0 16c-1.8 0-3.5-.6-4.9-1.7L18.3 7.1A7.9 7.9 0 0 1 12 20',
  ac: 'M22 11h-4.2l3.2-3.2-1.4-1.4L15 11h-2V9l4.7-4.7-1.4-1.4L13 6.2V2h-2v4.2L7.8 2.9 6.3 4.3 11 9v2H9L4.3 6.3 2.9 7.8 6.2 11H2v2h4.2l-3.2 3.2 1.4 1.4L9 13h2v2l-4.7 4.7 1.4 1.4L11 17.8V22h2v-4.2l3.2 3.2 1.4-1.4L13 15v-2h2l4.7 4.7 1.4-1.4L17.8 13H22z',
  business: 'M20 6h-4V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2zm-6 0h-4V4h4v2z',
  elevator: 'M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm2.5 7L7 12h5L9.5 9zm5 6L17 12h-5l2.5 3z',
  clock: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20m1 11h-5v-2h3V7h2z',
  pets: 'M4.5 9.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4m5-3a2 2 0 1 0 0-4 2 2 0 0 0 0 4m5 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4m5 3a2 2 0 1 0 0-4 2 2 0 0 0 0 4m-3.6 4.4c-.9-1-1.6-1.9-2.5-2.9-.6-.7-1.4-1.1-2.4-1.1s-1.8.4-2.4 1.1c-.9 1-1.6 1.9-2.5 2.9-1.3 1.3-2.9 2.8-2.6 4.8.3 1 1 2 2.3 2.3.7.1 3-.4 5.2-.4s4.5.5 5.2.4c1.3-.3 2-1.3 2.3-2.3.3-2-1.3-3.5-2.6-4.8z',
  transfer: 'M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z',
  tv: 'M21 3H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5v2h8v-2h5a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 14H3V5h18v12z',
  wheelchair: 'M12 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4M21 9h-6v13h-2v-6h-2v6H9V9H3V7h18v2z',
  bell: 'M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2m6-6V11a6 6 0 0 0-5-5.9V4a1 1 0 0 0-2 0v1.1A6 6 0 0 0 6 11v5l-2 2v1h16v-1z',
  luggage: 'M9 3a1 1 0 0 0-1 1v2H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2 1 1 0 0 0 2 0h8a1 1 0 0 0 2 0 2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-2V4a1 1 0 0 0-1-1zm1 2h4v1h-4zM8 9h1v9H8zm3.5 0h1v9h-1zm3.5 0h1v9h-1z',
  locker: 'M6 2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2m4 4v1.5h4V6zm0 3.5v4h4v-4z',
  safe: 'M18 8h-1V6a5 5 0 0 0-10 0v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2M9 6a3 3 0 0 1 6 0v2H9z',
  ticket: 'M22 10V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v4a2 2 0 0 1 0 4v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 1 0-4m-9 7.5h-2v-2h2zm0-4.5h-2v-2h2zm0-4.5h-2v-2h2z',
};

const RULES = [
  [/wi-?fi|internet/i, 'wifi'],
  [/pool|swim/i, 'pool'],
  [/bar|lounge|\bpub\b/i, 'bar'],
  [/breakfast/i, 'coffee'],
  [/coffee|caf[eé]|\btea\b/i, 'coffee'],
  [/restaurant|dining|dinner|food|room service/i, 'restaurant'],
  [/valet|parking/i, 'parking'],
  [/gym|fitness|workout/i, 'gym'],
  [/spa|massage|sauna|wellness|steam|jacuzzi/i, 'spa'],
  [/laundr|dry[\s-]?clean|ironing/i, 'laundry'],
  [/smoke|smoking/i, 'smoke'],
  [/air[\s-]?cond|climate|\ba\/?c\b/i, 'ac'],
  [/banquet|conference|meeting|business|event|\bhall\b/i, 'business'],
  [/lift|elevator/i, 'elevator'],
  [/wheelchair|accessible|disabled|handicap/i, 'wheelchair'],
  [/concierge|bell\s?(boy|desk)|butler/i, 'bell'],
  [/luggage|baggage|storage/i, 'luggage'],
  [/locker/i, 'locker'],
  [/safe|deposit|vault|valuables/i, 'safe'],
  [/tour|ticket|sightsee|excursion|activit/i, 'ticket'],
  [/reception|front desk|24[\s-]?hour|check[\s-]?in/i, 'clock'],
  [/\bpet|dog|animal/i, 'pets'],
  [/airport|shuttle|transfer|transport|pick[\s-]?up/i, 'transfer'],
  [/\btv\b|television|cable|flat[\s-]?screen/i, 'tv'],
];

export function amenityIcon(name = '') {
  for (const [re, key] of RULES) if (re.test(name)) return ICONS[key];
  return ICONS.check;
}
